import { useCallback } from "react";
import { parseMt5CsvInWorker } from "@/lib/lytick/csv-import-worker";
import type { ParsedDataset } from "@/lib/lytick/csv-import";
import { replaceChartSource } from "@/lib/lytick/chart-source-service";
import { loadDataset } from "@/lib/lytick/chart-data-service";
import { buildAllTiers } from "@/lib/lytick/aggregate-columns";
import { loadDrawingsForHash } from "@/lib/lytick/drawing-persistence";
import { db } from "@/lib/lytick/db";
import { useBootStore } from "@/stores/bootStore";
import { useChartStore } from "@/stores/chartStore";
import { useSessionStore } from "@/stores/sessionStore";
import { useUIStore } from "@/stores/uiStore";
import { useSettingsStore } from "@/stores/settingsStore";
import type { CustomCsvSymbol } from "@/stores/settingsStore";
import { useReplayStore } from "@/stores/replayStore";
import { requestPersistentStorage } from "@/lib/lytick/persist-storage";

/** Returns a random epoch snapped to the granularity, in the middle 60% of the range. */
export function randomMid60(firstTs: number, lastTs: number, granularitySeconds: number): number {
  const range = lastTs - firstTs;
  const lo = firstTs + range * 0.2;
  const hi = firstTs + range * 0.8;
  const raw = lo + Math.random() * (hi - lo);
  const grainMs = granularitySeconds * 1000;
  return Math.round(raw / grainMs) * grainMs;
}

/** Phase B — apply a parsed dataset to the chart and enter replay mode. */
export function commitCsvImport(
  dataset: ParsedDataset,
  symbol: string,
  opts?: { replayStartTs?: number | null },
): void {
  // 1. Load columnar data into chart-data-service (must be first)
  const tiers = dataset.tiers ?? buildAllTiers(dataset.columns, dataset.granularitySeconds);
  loadDataset(dataset.contentHash, symbol, dataset.granularitySeconds, dataset.columns, tiers);

  // 2. Update chart store symbol (resets hasData flag, must come before replaceChartSource)
  useChartStore.getState().setSymbol(symbol);

  // 3. Signal chart-runtime to rebuild (thin event — no candle array)
  replaceChartSource({
    symbol,
    granularitySeconds: dataset.granularitySeconds,
    firstTs: dataset.firstTs,
    lastTs: dataset.lastTs,
  });

  const savedDrawings = loadDrawingsForHash(dataset.contentHash);
  useSessionStore.getState().setPendingRestoreDrawings(
    savedDrawings?.length ? savedDrawings : null,
  );

  useBootStore.getState().setPhase("ready");

  // 4. Persist manifest to Dexie (candle data is in OPFS or already stored by the worker)
  db.csvDatasets
    .put({
      contentHash: dataset.contentHash,
      manifest: {
        contentHash: dataset.contentHash,
        symbol,
        granularitySeconds: dataset.granularitySeconds,
        rowCount: dataset.rowCount,
        firstTs: dataset.firstTs,
        lastTs: dataset.lastTs,
        savedAt: Date.now(),
        tierTfMinutes: Array.from(tiers.keys()),
      },
      savedAt: Date.now(),
    })
    .catch(() => {
      useUIStore
        .getState()
        .addToast("Couldn't save dataset manifest for reload — it will need re-importing.", "error");
    });

  const { addCustomCsvSymbol, setLastActiveCsvHash } = useSettingsStore.getState();
  addCustomCsvSymbol({
    contentHash: dataset.contentHash,
    symbol,
    granularitySeconds: dataset.granularitySeconds,
    firstTs: dataset.firstTs,
    lastTs: dataset.lastTs,
    rowCount: dataset.rowCount,
    addedAt: Date.now(),
  });
  setLastActiveCsvHash(dataset.contentHash);

  // Request durable storage so the browser won't evict imported datasets under disk pressure.
  void requestPersistentStorage();

  try {
    sessionStorage.setItem(
      "lytick-csv-meta",
      JSON.stringify({
        contentHash: dataset.contentHash,
        symbol,
        granularitySeconds: dataset.granularitySeconds,
        rowCount: dataset.rowCount,
        firstTs: dataset.firstTs,
        lastTs: dataset.lastTs,
      }),
    );
  } catch {
    // sessionStorage unavailable — not critical
  }

  useUIStore.getState().clearPendingCsvImport();

  if (opts?.replayStartTs != null) {
    useReplayStore.getState().setReplayData(
      opts.replayStartTs,
      dataset.lastTs,
      dataset.granularitySeconds,
    );
  }
}

/** Load a previously-saved CSV symbol from OPFS (or Dexie fallback) and apply it. */
export async function loadAndCommitCsvSymbol(meta: CustomCsvSymbol): Promise<boolean> {
  const { readFullDatasetFromOpfs, isOpfsAvailable } = await import("@/lib/lytick/opfs-store");

  if (isOpfsAvailable()) {
    const stored = await readFullDatasetFromOpfs(meta.contentHash);
    if (stored) {
      commitCsvImport(
        {
          columns: stored.base,
          tiers: stored.tiers,
          symbol: meta.symbol,
          granularitySeconds: meta.granularitySeconds,
          rowCount: meta.rowCount,
          firstTs: meta.firstTs,
          lastTs: meta.lastTs,
          contentHash: meta.contentHash,
          warnings: [],
          usedFallback: false,
          headers: [],
          columnMap: { dateIdx: 0, timeIdx: 1, openIdx: 2, highIdx: 3, lowIdx: 4, closeIdx: 5, volumeIdx: 6 },
        },
        meta.symbol,
        { replayStartTs: null },
      );
      return true;
    }
  }

  // Dexie fallback
  const { deserializeColumns } = await import("@/lib/lytick/columnar");
  const stored = await db.csvDatasets.get(meta.contentHash);
  if (!stored?.baseBuffer) return false;

  try {
    const base = deserializeColumns(stored.baseBuffer);
    commitCsvImport(
      {
        columns: base,
        symbol: meta.symbol,
        granularitySeconds: meta.granularitySeconds,
        rowCount: meta.rowCount,
        firstTs: meta.firstTs,
        lastTs: meta.lastTs,
        contentHash: meta.contentHash,
        warnings: [],
        usedFallback: false,
        headers: [],
        columnMap: { dateIdx: 0, timeIdx: 1, openIdx: 2, highIdx: 3, lowIdx: 4, closeIdx: 5, volumeIdx: 6 },
      },
      meta.symbol,
      { replayStartTs: null },
    );
    return true;
  } catch {
    return false;
  }
}

export function useCsvImport() {
  const setPhase = useBootStore((s) => s.setPhase);
  const setProgress = useBootStore((s) => s.setLoadProgress);
  const setError = useBootStore((s) => s.setError);
  const setPendingCsvImport = useUIStore((s) => s.setPendingCsvImport);
  const autoLoadCsv = useSettingsStore((s) => s.autoLoadCsv);

  const importFile = useCallback(
    async (file: File) => {
      if (!file.name.toLowerCase().endsWith(".csv")) {
        setError("Only CSV files are supported.");
        return;
      }

      // No size cap — large files are handled via columnar streaming + OPFS

      setPhase("parsing");
      setProgress(0);

      try {
        const dataset = await parseMt5CsvInWorker(file, (pct) => setProgress(pct));

        if (autoLoadCsv) {
          const replayStartTs = randomMid60(dataset.firstTs, dataset.lastTs, dataset.granularitySeconds);
          commitCsvImport(dataset, dataset.symbol, { replayStartTs });
        } else {
          setPhase("ready");
          setPendingCsvImport({ file, dataset });
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to parse CSV.");
      }
    },
    [setPhase, setProgress, setError, setPendingCsvImport, autoLoadCsv],
  );

  return { importFile };
}
